// @flow

import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { matchPath } from 'react-router';
import { useLocation } from 'react-router-dom';

import { ADMIN_APP_ASSET_PATHS } from 'src/constants/assets';
import { getAssetURL } from 'src/helpers';
import { isInWhiteListTurboBanner } from 'src/helpers/whitelist';
import FormerModal from '../common/upgrade-turbo-campaign/former-modal';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import TurboModal from '../common/upgrade-turbo-campaign/turbo-modal';
import { routes } from 'src/routes';

interface Props extends WithI18nProps {
  tenantApp: any;
  isLiveThemeIntegratedTurbo: boolean;
}

const WarningTurbo: React.FC<Props> = ({ i18n, tenantApp, isLiveThemeIntegratedTurbo }) => {
  const location = useLocation();
  const [isShowFormerModal, setIsShowFormerModal] = React.useState(false);
  const [isShowTurboModal, setIsShowTurboModal] = React.useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const isOpenFormerModal = searchParams.get('isOpenFormerModal');
  const isOpenTurboModal = searchParams.get('isOpenTurboModal');

  useEffect(() => {
    if (isOpenFormerModal) {
      setIsShowFormerModal(true);
    } else if (isOpenTurboModal) {
      setIsShowTurboModal(true);
    }
  }, []);

  const isInWhiteListBanner = useMemo(() => {
    return isInWhiteListTurboBanner(tenantApp.tenantId) && !isLiveThemeIntegratedTurbo;
  }, [tenantApp, isLiveThemeIntegratedTurbo]);

  const textFormer = useMemo(() => {
    let t2Text = '';

    // Page search
    if (matchPath(location.pathname, '/search/*')) {
      t2Text = i18n.translate('AdminHTML.Common.WarningFormer.titleByFeature_Search');
    }

    // Page analytics
    if (
      location.pathname.includes('analytic') ||
      matchPath(location.pathname, routes.analytics.appImpact())
    ) {
      t2Text = i18n.translate('AdminHTML.Common.WarningFormer.titleByFeature_Analytics');
    }

    // Page recommendation
    if (
      [
        `/recommendation-widgets`,
        routes.predictiveBundle.list(),
        routes.recommendationGeneralSettings.settings()
      ].some(path => matchPath(location.pathname, path))
    ) {
      t2Text = i18n.translate('AdminHTML.Common.WarningFormer.titleByFeature_Recommendation');
    }

    if (t2Text) {
      return (
        <p>
          {i18n.translate('AdminHTML.Common.WarningFormer.titleByFeature', {
            t1: <b>{i18n.translate('AdminHTML.Common.WarningFormer.turbo')}</b>,
            t2: <b>{t2Text}</b>,
            t3: (
              <span
                className="Polaris-Link"
                role="button"
                onClick={() => setIsShowFormerModal(true)}>
                {i18n.translate('AdminHTML.Common.WarningFormer.seeMoreDetail')}
              </span>
            )
          })}
        </p>
      );
    }

    return (
      <p>
        {i18n.translate('AdminHTML.Common.WarningFormer.title', {
          t1: <b>{i18n.translate('AdminHTML.Common.WarningFormer.title_t1')}</b>,
          t2: <b>{i18n.translate('AdminHTML.Common.WarningFormer.title_t2')}</b>,
          t3: (
            <span className="Polaris-Link" role="button" onClick={() => setIsShowFormerModal(true)}>
              {i18n.translate('AdminHTML.Common.WarningFormer.seeMoreDetail')}
            </span>
          )
        })}
      </p>
    );
  }, [i18n, location.pathname]);

  if (!isInWhiteListBanner) {
    return null;
  }

  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);

  if (
    !isEnableShopifyIntegration &&
    [routes.backInStock.subscription(), routes.appPreferences.view()].some(path =>
      matchPath(location.pathname, path)
    )
  ) {
    return null;
  }

  return (
    <div>
      <div className="BC-Warning-TurboBanner">
        <img
          src={getAssetURL(ADMIN_APP_ASSET_PATHS?.common?.warningTriangle)}
          width={20}
          height={20}
          alt="warning"
        />
        {isEnableShopifyIntegration ? (
          <p>
            {i18n.translate('AdminHTML.Common.WarningTurbo.title', {
              t1: <b>{i18n.translate('AdminHTML.Common.WarningTurbo.title_t1')}</b>,
              t2: <b>{i18n.translate('AdminHTML.Common.WarningTurbo.title_t2')}</b>,
              t3: <b>{i18n.translate('AdminHTML.Common.WarningTurbo.title_t3')}</b>,
              t4: (
                <span
                  className="Polaris-Link"
                  role="button"
                  onClick={() => setIsShowTurboModal(true)}>
                  {i18n.translate('AdminHTML.Common.WarningTurbo.seeMoreDetail')}
                </span>
              )
            })}
          </p>
        ) : (
          textFormer
        )}
      </div>
      <FormerModal active={isShowFormerModal} onCancel={() => setIsShowFormerModal(false)} />
      <TurboModal active={isShowTurboModal} onCancel={() => setIsShowTurboModal(false)} />
    </div>
  );
};

const mapStateToProps = state => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    tenantApp,
    isLiveThemeIntegratedTurbo: _.get(state.themeIntegration, 'isLiveThemeIntegratedTurbo', true)
  };
};

export default connect(mapStateToProps)(withI18n()(WarningTurbo));
